import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/common/PageContainer";
import ChecklistStepper from "../components/checklist/ChecklistStepper";
import ChecklistForm from "../components/checklist/ChecklistForm";
import LoadingState from "../components/common/LoadingState";
import ErrorState from "../components/common/ErrorState";
import { checklistService } from "../services/checklistService";
import { Alert, Snackbar } from "@mui/material";
import ConfirmationDialog from "../components/common/ConfirmationDialog";

/**
 * Page component for viewing and editing a checklist
 */
const TechnicianChecklistView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [fsmParams, setFsmParams] = useState({});

  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        setLoading(true);
        setError(null);

        // Extract URL parameters
        const serviceAppointmentNumber = searchParams.get("saId");
        const caseId = searchParams.get("caseId");
        const templateId = searchParams.get("templateId");
        const templateVersion = searchParams.get("templateVersion") || 1;
        const status = searchParams.get("workStepId");
        const perEquipment = searchParams.get("perEquipment");

        // Store FSM parameters for reference
        setFsmParams({
          serviceAppointmentNumber: serviceAppointmentNumber,
          caseId: caseId,
          templateId: templateId,
          templateVersion: templateVersion,
          status: status,
          perEquipment: perEquipment,
        });

        // Validate required parameters
        if (!serviceAppointmentNumber) {
          throw new Error("Service Appointment ID (saId) is required");
        }
        if (!templateId) {
          throw new Error("Template ID (templateId) is required");
        }
        // if (!templateVersion) {
        //   throw new Error("Template version is required");
        // }

        // Map URL parameters to checklistService format
        const queryParams = {
          saId: serviceAppointmentNumber,
          templateId: templateId,
          templateVersion: templateVersion || 1,
        };

        console.log("Fetching checklist with params:", queryParams);

        // Fetch checklist from API
        const checklistData = await checklistService.getChecklist(queryParams);

        console.log("Received checklist data:", checklistData);

        // Transform data for display (add title if not present)
        const displayChecklist = {
          ...checklistData,
          // title: checklistData.name || `${serviceAppointmentNumber}, ${templateId}`,
        };

        setChecklist(displayChecklist);
      } catch (err) {
        console.error("Error fetching checklist:", err);
        setError(err.message || "Failed to load checklist. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, [searchParams]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = (templateId) => {
    // In a real application, we would delete the checklist via the API
    console.log("Deleting checklist:", templateId);

    // Navigate back to the checklists page
    navigate("/checklists");
  };

  const handleDuplicate = (templateId) => {
    // In a real application, we would duplicate the checklist via the API
    console.log("Duplicating checklist:", templateId);

    // Navigate to the new checklist
    navigate("/checklists/new");
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleUpdateItem = (
    templateId,
    workOrderLineItemId,
    itemId,
    updates
  ) => {
    console.log(
      "Updating item:",
      itemId,
      "in the asset: ",
      workOrderLineItemId,
      "in checklist:",
      templateId,
      "with:",
      updates
    );
    console.log("Checklist before updates", checklist);
    const newFsmAssets = checklist.fsmAssets.map((asset) => {
      if (asset.work_order_line_item_id === workOrderLineItemId) {
        const newItems = asset.template_config.items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              completed: !updates.textValue ? false : true,
              ...updates,
            };
          } else {
            return item;
          }
        });
        return {
          ...asset,
          template_config: { ...asset.template_config, items: newItems },
        };
      } else {
        return asset;
      }
    });

    const newChecklist = { ...checklist, fsmAssets: newFsmAssets };
    console.log("Updated checklist", newChecklist);
    setChecklist(newChecklist);
    // Update the local state immediately for better UX
    // setChecklist((prevChecklist) => ({
    //   ...prevChecklist,
    //   fsmAssets: prevChecklist.fsmAssets.map((asset) =>
    //     asset.assetSerialNumber === assetSerialNumber
    //       ? asset.template_config.items.map((item) =>
    //           item.id === itemId ? { ...item, ...updates } : item
    //         )
    //       : asset
    //   ),
    // }));

    // TODO: Optionally implement auto-save or batch updates
    // For now, changes will be saved when user explicitly saves
  };

  const handlePdfDialogOpen = (openDialog) => {
    setPdfDialogOpen(openDialog);
  };

  const handleSave = async (updatedChecklist) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Saving updated checklist:", updatedChecklist);

      // Prepare checklist data for backend
      const checklistToSave = {
        ...updatedChecklist,
        // Ensure required fields from FSM parameters
        templateId: fsmParams.templateId,
        templateVersion: fsmParams.templateVersion,
        serviceAppointmentNumber: fsmParams.serviceAppointmentNumber,
        // Update timestamp
        updatedAt: new Date().toISOString(),
      };

      // Save to backend
      const saveResponse = await checklistService.saveChecklist(
        checklistToSave,
        fsmParams.status
      );

      console.log("Checklist saved successfully:", saveResponse);

      // Update local state with saved data
      setChecklist(updatedChecklist);
      setIsEditing(false);

      // Optional: Show success message
      // You could add a toast notification here
    } catch (err) {
      console.error("Error saving checklist:", err);
      setError(err.message || "Failed to save checklist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePdfConfirm = async () => {
    try {
      // Extract parameters needed for PDF generation
      const pdfParams = {
        caseId: fsmParams.caseId || "",
        serviceAppointmentNumber: fsmParams.serviceAppointmentNumber || "",
        workStepId: fsmParams.status || "",
      };

      console.log("Generating PDF with params:", pdfParams);

      // Call the PDF service
      await checklistService.savePdf(pdfParams);

      // Show success message
      setSnackbar({
        open: true,
        message: "PDF saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving PDF:", error);
      setSnackbar({
        open: true,
        message: "Failed to save PDF. Please try again.",
        severity: "error",
      });
    }
  };

  const handlePdfCancel = () => {
    // Show the regular success message when PDF is cancelled
    setSnackbar({
      open: true,
      message: "Checklist saved successfully!",
      severity: "success",
    });
  };

  const handleClosePdfDialog = () => {
    console.log("handleClosePdfDialog called - closing dialog");
    setPdfDialogOpen(false);
  };

  // const handleSavePdf = async (
  //   fsmParams,
  //   checklistProgress,
  //   layoutSavingStatus
  // ) => {};

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (loading) {
    return (
      <PageContainer title="Loading Checklist...">
        <LoadingState />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Error">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </PageContainer>
    );
  }

  if (!checklist) {
    return (
      <PageContainer title="Checklist Not Found">
        <ErrorState
          message="The requested checklist could not be found."
          onRetry={() => navigate("/checklists")}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ChecklistStepper
        checklist={checklist}
        onUpdateItem={handleUpdateItem}
        onSave={handleSave}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onSetPdfDialogOpen={handlePdfDialogOpen}
        // fsmParams={fsmParams}
        itemsPerStep={6}
        allowSkipping={true}
        requireStepCompletion={false}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        onConfirm={handlePdfConfirm}
        title="Færdig?"
        // message="You've completed this checklist. Would you like to save the PDF into Salesforce? Once done you can close this page and go back to the FSM App."
        // message="Du er ved at færdiggøre checklisten. Er du færdig ?"
        message="Du er ved at færdiggøre checklisten. Er du færdig? Når du har klikket på Ja, skal du lukke tjeklistesiden og gå tilbage til FSM-appen."
        confirmText="Ja"
        cancelText="Skip"
        confirmColor="primary"
      />
    </PageContainer>
  );
};

export default TechnicianChecklistView;
